import { FirebaseAdmin } from 'fitbeat.firebase';
import {
  BaseUserProfile,
  GymEnvironment,
  HandledError,
  IFamilyPlanInvitation,
  InvitationCode,
  InvitationCodeType,
  profileColors,
} from 'fitbeat.models';
import { NotificationService } from 'fitbeat.notification-service';
import { FamilyPlanConfig } from '../../config/familyplanconfig';
import { UserProfile } from '../../entities';
import { IInvitationCodeRepository, IUserProfileRepository } from '../../persistence';
import { AccountStatusManager } from '../accountstatus/accountstatusmanager';
import { IInvitationCodeGenerator } from './../../helpers/IInvitationCodeGenerator';

export class FamilyPlanService {
  public static assignAvailableProfileColor = (
    userProfile: UserProfile, colorsUsedByOtherUsers: string[]): void => {
    const availableColors = profileColors.filter((color) => {
      return !colorsUsedByOtherUsers.includes(color);
    });
    userProfile.familyPlanSettings = {
      profileColor: availableColors.length > 0 ? availableColors[0] : '#000000',
    };
  }

  private userProfileRepository: IUserProfileRepository;
  private invitationCodeRepository: IInvitationCodeRepository;
  private invitationCodeGenerator: IInvitationCodeGenerator;
  private firebaseAdmin: FirebaseAdmin;
  private accountStatusManager: AccountStatusManager;
  private notificationSevice: NotificationService;

  constructor(
    userProfileRepopository: IUserProfileRepository,
    invitationCodeRepository: IInvitationCodeRepository,
    invitationCodeGenerator: IInvitationCodeGenerator,
    firebaseAdmin: FirebaseAdmin,
    accountStatusManager: AccountStatusManager,
    notificationSevice: NotificationService,
  ) {
    this.userProfileRepository = userProfileRepopository;
    this.invitationCodeRepository = invitationCodeRepository;
    this.invitationCodeGenerator = invitationCodeGenerator;
    this.firebaseAdmin = firebaseAdmin;
    this.accountStatusManager = accountStatusManager;
    this.notificationSevice = notificationSevice;
  }

  public getAllUsersInFamilyPlan = async (uid: string): Promise<UserProfile[]> => {
    const currentUserProfile = await this.userProfileRepository.get(uid);

    if (!currentUserProfile) {
      throw new Error(`user not found for ${uid}`);
    }

    const planOwnerUserProfileId = currentUserProfile.planOwnerUserProfileId
      ? currentUserProfile.planOwnerUserProfileId
      : currentUserProfile.uid;

    const userAndFamilyMembers = await this.userProfileRepository.getAllUsersInFamilyPlan(planOwnerUserProfileId);

    const planOwnerUserProfile = userAndFamilyMembers.find((user) => user.uid === planOwnerUserProfileId);

    if (!planOwnerUserProfile) {
      throw new Error(`parent user not found for ${uid}`);
    }

    if (!planOwnerUserProfile.isFamilyPlanAdmin) {
      throw new Error(`user with id ${planOwnerUserProfileId} is not a family member admin`);
    }

    userAndFamilyMembers.map((user) => {
      if (!user.isFamilyPlanAdmin) {
        user.isFamilyPlanMember = true;
      }

      if (user.familyPlanSettings && user.familyPlanSettings.pin) {
        delete user.familyPlanSettings.pin;
      }

      return user;
    });

    return userAndFamilyMembers;
  }

  public canFamilyPlanAdminInviteMoreMembers = (
    familyMembers: UserProfile[],
    invitedFamilyMembers: IFamilyPlanInvitation[],
  ) => {
    const currentAmountOfFamilyMembersWithInvites =
      familyMembers.length + invitedFamilyMembers.length;

    return currentAmountOfFamilyMembersWithInvites < FamilyPlanConfig.determineMaxMembersForFamilyPlan();
  }

  public inviteUserToFamilyPlan = async (familyPlanOwnerUserProfileId: string, userEmailToInvite: string) => {
    const familyPlanOwnerUserProfile = await this.userProfileRepository.get(familyPlanOwnerUserProfileId);

    if (!familyPlanOwnerUserProfile) {
      throw new Error(`user not found for ${familyPlanOwnerUserProfileId}`);
    }

    if (!familyPlanOwnerUserProfile.isFamilyPlanAdmin) {
      throw new Error(`only family plan admins can send invites`);
    }

    const [userAndFamilyMembers, invitedFamilyMembers] = await Promise.all([
      this.getAllUsersInFamilyPlan(familyPlanOwnerUserProfileId),
      this.getAllInvitedUsersInFamilyPlan(familyPlanOwnerUserProfileId),
    ]);

    const canInviteMoreFamilyMembers =
      this.canFamilyPlanAdminInviteMoreMembers(
        userAndFamilyMembers,
        invitedFamilyMembers,
      );

    const existingFamilyMemberInvited = invitedFamilyMembers.find(
      (inv) => inv.email.toLowerCase() === userEmailToInvite.toLowerCase(),
    );

    if (existingFamilyMemberInvited) {
      await this.notificationSevice.sendLinkToAcceptFamilyInvite(familyPlanOwnerUserProfileId,
        existingFamilyMemberInvited.email, existingFamilyMemberInvited.code);
      return;
    }

    if (!canInviteMoreFamilyMembers) {
      const maxMembers = FamilyPlanConfig.determineMaxMembersForFamilyPlan();
      const maximumReachedErrorMessage = `You have reached the member limit of ${maxMembers} on your current plan.`;
      const contactSupportErrorMessage = 'Contact support to upgrade your plan.';
      throw new HandledError(
        `${maximumReachedErrorMessage} ${contactSupportErrorMessage}`);
    }

    const invitationCode: InvitationCode = {
      code: this.invitationCodeGenerator.generate(),
      redeemable: true,
      cancelled: false,
      invitationCodeDetails: {
        type: InvitationCodeType.familyPlan,
        familyPlanDetails: {
          planOwnerUserProfileId: familyPlanOwnerUserProfileId,
          invitedEmail: userEmailToInvite,
        },
      },
    };
    await this.notificationSevice.sendLinkToAcceptFamilyInvite(familyPlanOwnerUserProfileId,
      userEmailToInvite, invitationCode.code);
    await this.invitationCodeRepository.add(invitationCode);
  }

  public cancelPendingFamilyPlanInvitation = async (code: string) => {
    const invitationCode = await this.invitationCodeRepository.get(code);

    if (!invitationCode) {
      throw new Error(`invitation code ${code} not found`);
    }

    await this.invitationCodeRepository.cancelInvitationCode(invitationCode);
  }

  public acceptFamilyPlanInvite = async (
    currentLoggedInUserProfileId: string,
    invitationCode: string,
    gymEnvironment: GymEnvironment,
  ): Promise<any> => {
    const invitationCodeDetails = await this.invitationCodeRepository.get(invitationCode);

    if (!invitationCodeDetails) {
      throw new Error(`invitation code not found ${invitationCode}`);
    }

    if (!invitationCodeDetails.redeemable) {
      throw new Error(`invitation code is no longer redeemable ${invitationCode}`);
    }

    if (invitationCodeDetails.cancelled) {
      throw new Error(`invitation code ${invitationCode} is cancelled`);
    }

    if (
      !invitationCodeDetails.invitationCodeDetails ||
      !invitationCodeDetails.invitationCodeDetails.familyPlanDetails
    ) {
      throw new Error(`incomplete invitation code details ${JSON.stringify(invitationCode)}`);
    }

    const userAndFamilyMembers = await this.getAllUsersInFamilyPlan(
      invitationCodeDetails.invitationCodeDetails.familyPlanDetails
        .planOwnerUserProfileId,
    );
    const canJoinFamilyPlan = this.canFamilyPlanAdminInviteMoreMembers(userAndFamilyMembers, []);

    if (!canJoinFamilyPlan) {
      throw new Error(`cannot accept invitation as maximum allowed members are in the family plan`);
    }

    const familyPlanInvitationCodeDetails = invitationCodeDetails.invitationCodeDetails.familyPlanDetails;

    const familyPlanOwnerUserProfile = await this.userProfileRepository.get(
      familyPlanInvitationCodeDetails.planOwnerUserProfileId,
    );

    if (!familyPlanOwnerUserProfile) {
      throw new Error(`user not found for ${familyPlanInvitationCodeDetails.planOwnerUserProfileId}`);
    }

    const currentLoggedInUserProfile = await this.userProfileRepository.get(currentLoggedInUserProfileId);

    if (!currentLoggedInUserProfile) {
      throw new Error(`user not found for ${currentLoggedInUserProfileId}`);
    }

    const accountChangeStatus = await this.accountStatusManager.changeAccountStatus(
      currentLoggedInUserProfile,
      'FamilySubscription',
      gymEnvironment,
      familyPlanOwnerUserProfile.uid,
    );

    if (accountChangeStatus.success) {
      invitationCodeDetails.redeemable = false;
      await Promise.all([this.invitationCodeRepository.update(invitationCodeDetails),
        this.assignDefaultProfileColor(familyPlanOwnerUserProfile.uid, currentLoggedInUserProfile.uid)]);
    }

    return {
      message: accountChangeStatus.message,
      success: accountChangeStatus.success,
    };
  }

  public getAllInvitedUsersInFamilyPlan = async (planOwnerUserProfileId: string): Promise<IFamilyPlanInvitation[]> => {
    const planOwnerUserProfile = await this.userProfileRepository.get(planOwnerUserProfileId);

    if (!planOwnerUserProfile) {
      throw new Error(`user not found for ${planOwnerUserProfileId}`);
    }

    if (!planOwnerUserProfile.isFamilyPlanAdmin) {
      return [];
    }

    const invitations =
      await this.invitationCodeRepository.getInvitationCodesFromFamilyPlanOwner(planOwnerUserProfileId);

    return invitations.map((invitation) => {
      if (!invitation.invitationCodeDetails || !invitation.invitationCodeDetails.familyPlanDetails) {
        throw new Error(`missing family plan details on invitation code ${invitation.code}`);
      }

      const familyMemberInvitation: IFamilyPlanInvitation = {
        email: invitation.invitationCodeDetails.familyPlanDetails.invitedEmail!,
        code: invitation.code,
      };

      return familyMemberInvitation;
    });
  }

  public switchFamilyMembers = async (
    currentLoggedInUserId: string,
    userIdToSwopTo: string,
    pin: string,
  ): Promise<string> => {
    const [currentUser, userToImpersonate] = await Promise.all([
      this.userProfileRepository.get(currentLoggedInUserId),
      this.userProfileRepository.get(userIdToSwopTo),
    ]);

    if (!userToImpersonate) {
      throw new Error(`user not found for ${userIdToSwopTo}`);
    }

    if (!currentUser) {
      throw new Error(`user not found for ${currentLoggedInUserId}`);
    }

    const { familyPlanSettings } = userToImpersonate;
    const profileIsProtectedByPin = BaseUserProfile.isProfileProtectedByPin(userToImpersonate);

    if (familyPlanSettings && profileIsProtectedByPin && pin !== familyPlanSettings.pin) {
      throw new Error('Invalid pin supplied');
    }

    if (currentUser.uid !== userToImpersonate.uid) {
      const usersCanSwopTokens = BaseUserProfile.isPartOfSameFamilyPlan(currentUser, userToImpersonate);

      if (!usersCanSwopTokens) {
        throw new Error(`users can only swop tokens with users in the same family plan`);
      }
    }

    const token = await this.firebaseAdmin.generateTokenForUserId(userIdToSwopTo);

    return token;
  }

  public removeUserFromFamilyPlan = async (
    currentLoggedInUserId: string,
    userProfileIdToRemove: string,
  ): Promise<void> => {
    const familyMemberToRemove = await this.userProfileRepository.get(userProfileIdToRemove);
    const currentLoggedInUser = await this.userProfileRepository.get(currentLoggedInUserId);

    if (!familyMemberToRemove) {
      throw new Error(`user not found for ${userProfileIdToRemove}`);
    }

    if (!currentLoggedInUser) {
      throw new Error(`user not found for ${userProfileIdToRemove}`);
    }

    if (!BaseUserProfile.isPartOfFamilyPlan(currentLoggedInUser)) {
      throw new Error(`only a user in a family plan may remove a family plan member`);
    }

    if (familyMemberToRemove.isFamilyPlanAdmin) {
      throw new Error(`family plan admins can not be removed from a family plan`);
    }

    if (currentLoggedInUser.isFamilyPlanAdmin) {
      if (BaseUserProfile.isPartOfSameFamilyPlan(currentLoggedInUser, familyMemberToRemove)) {
        await this.removeFamilyMemberFromFamilyPlan(familyMemberToRemove);
      } else {
        throw new Error(`family plan admins can only remove members in the same family plan`);
      }
    }

    if (currentLoggedInUser.planOwnerUserProfileId) {
      if (userProfileIdToRemove === currentLoggedInUserId) {
        await this.removeFamilyMemberFromFamilyPlan(familyMemberToRemove);
      } else {
        throw new Error(`family plan members may only remove themselves`);
      }
    }
  }

  private removeFamilyMemberFromFamilyPlan = async (familyMemberToRemove: UserProfile) => {
    delete familyMemberToRemove.planOwnerUserProfileId;
    delete familyMemberToRemove.planOwnerStatus;
    delete familyMemberToRemove.familyPlanSettings;

    await this.userProfileRepository.update(familyMemberToRemove);
  }

  private assignDefaultProfileColor = async (
    familyPlanOwnerUserProfileId: string, currentLoggedInUserProfileUid: string): Promise<void> => {
    const [usersInFamilyPlan, currentLoggedInUserProfile] = await Promise.all(
      [
        this.getAllUsersInFamilyPlan(familyPlanOwnerUserProfileId),
        this.userProfileRepository.get(currentLoggedInUserProfileUid),
      ]);
    const colorsUsedByOtherUsers = [];
    for (const user of usersInFamilyPlan) {
      if (user.familyPlanSettings && user.familyPlanSettings.profileColor) {
        colorsUsedByOtherUsers.push(user.familyPlanSettings.profileColor);
      }
    }
    if (currentLoggedInUserProfile) {
      FamilyPlanService.assignAvailableProfileColor(
        currentLoggedInUserProfile, colorsUsedByOtherUsers);
      await this.userProfileRepository.update(currentLoggedInUserProfile);
    }
  }
}
